/**
 * "บล็อกผู้ส่งนี้" (web chat) — the wording and rules the card, the settings list and the inbox chip share.
 * Pure functions (unit-tested in test/wcblock.spec.ts). Server rules: api/app/webchat/blocks.py.
 */
import type { BlockDuration, IpDuration, WebBlock, WebBlockState } from '../api/wcblock/types'

export const DURATIONS: { key: BlockDuration; label: string }[] = [
  { key: '24h', label: '24 ชั่วโมง' },
  { key: '7d', label: '7 วัน' },
  { key: 'forever', label: 'ถาวร' },
]
export const IP_DURATIONS: { key: IpDuration; label: string }[] = [
  { key: '1h', label: '1 ชั่วโมง' },
  { key: '24h', label: '24 ชั่วโมง' },
]
const DURATION_MS: Record<BlockDuration, number | null> = { '24h': 24 * 3600_000, '7d': 7 * 24 * 3600_000, forever: null }
export const REASON_MAX = 300
export const IP_WARNING =
  'เน็ตมือถือให้ลูกค้าหลายคนใช้ IP เดียวกัน การบล็อก IP อาจทำให้ลูกค้าคนอื่นที่ใช้เครือข่ายเดียวกันส่งข้อความไม่ได้ด้วย ใช้เฉพาะเมื่อผู้ส่งเปลี่ยนตัวตนมาสแปมซ้ำ'
export const MANAGER_ONLY = 'เฉพาะผู้จัดการหรือเจ้าของ'
/** The thread line when a block closed the chat (api/app/webchat/blocks.py BLOCK_CLOSE_REASON — the same text). */
export const BLOCK_CLOSE_REASON = 'ปิดแชทอัตโนมัติเพราะบล็อกผู้ส่ง'

const TH_MONTH = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
const BKK_MS = 7 * 3600_000

/** Bangkok date + time: "วันนี้ 14:30 น." · "พรุ่งนี้ 09:05 น." · "3 ต.ค. 14:30 น." (other year: "3 ต.ค. 70 …"). */
export function bkkDateTime(iso: string, nowMs: number): string {
  const t = new Date(Date.parse(iso) + BKK_MS)
  const n = new Date(nowMs + BKK_MS)
  const hm = `${String(t.getUTCHours()).padStart(2, '0')}:${String(t.getUTCMinutes()).padStart(2, '0')} น.`
  const day = (d: Date) => Math.floor(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 86400_000)
  const diff = day(t) - day(n)
  if (diff === 0) return `วันนี้ ${hm}`
  if (diff === 1) return `พรุ่งนี้ ${hm}`
  if (diff === -1) return `เมื่อวาน ${hm}`
  const year = t.getUTCFullYear() === n.getUTCFullYear() ? '' : ` ${String(t.getUTCFullYear() + 543).slice(-2)}`
  return `${t.getUTCDate()} ${TH_MONTH[t.getUTCMonth()]}${year} ${hm}`
}

/** "อีก 45 นาที" · "อีก 23 ชั่วโมง" · "อีก 6 วัน" (null when already past). */
export function remainingText(iso: string, nowMs: number): string | null {
  const ms = Date.parse(iso) - nowMs
  if (!(ms > 0)) return null
  const min = Math.ceil(ms / 60000)
  if (min < 60) return `อีก ${min} นาที`
  const h = Math.round(ms / 3600_000)
  if (h < 48) return `อีก ${h} ชั่วโมง`
  return `อีก ${Math.round(ms / 86400_000)} วัน`
}

/** "บล็อกถาวร" · "ถึง พรุ่งนี้ 14:30 น. (อีก 23 ชั่วโมง)". */
export function untilText(until: string | null, nowMs: number): string {
  if (until === null) return 'บล็อกถาวร'
  const left = remainingText(until, nowMs)
  return `ถึง ${bkkDateTime(until, nowMs)}${left ? ` (${left})` : ''}`
}

/** The inbox row chip is shown while the block runs (the list refreshes every 30 s; the clock hides it at once). */
export function blockedNow(b: { until: string | null } | null | undefined, nowMs: number): boolean {
  return !!b && (b.until === null || Date.parse(b.until) > nowMs)
}

export function chipTip(b: { until: string | null }, nowMs: number): string {
  return b.until === null ? 'บล็อกผู้ส่งนี้ถาวร' : `บล็อกผู้ส่งนี้ ${untilText(b.until, nowMs)}`
}

export interface DurationOption {
  key: BlockDuration
  label: string
  disabled: boolean
  why: string | null
}

/**
 * What this person may pick. Staff: 24 ชม. / 7 วัน, and while a block runs only something at least as long (never
 * shorter — a shorter one is an unblock in disguise); head/owner: everything.
 */
export function durationOptions(state: Pick<WebBlockState, 'block' | 'can'>, nowMs: number): DurationOption[] {
  const running = state.block
  return DURATIONS.map((d) => {
    if (d.key === 'forever' && !state.can.forever) return { ...d, disabled: true, why: MANAGER_ONLY }
    if (running && !state.can.unblock) {
      const ms = DURATION_MS[d.key]
      const longer = running.until === null ? false : ms === null || nowMs + ms >= Date.parse(running.until)
      if (!longer) return { ...d, disabled: true, why: 'สั้นกว่าที่บล็อกอยู่' }
    }
    return { ...d, disabled: false, why: null }
  })
}

/** The first duration this person may pick (the dialog's default). */
export function defaultDuration(state: Pick<WebBlockState, 'block' | 'can'>, nowMs: number): BlockDuration | null {
  return durationOptions(state, nowMs).find((o) => !o.disabled)?.key ?? null
}

/** "ชนเพดานกันสแปม 4 ครั้งใน 7 วัน · ล่าสุด 40 นาทีที่แล้ว" · "ไม่ชนเพดานกันสแปมใน 7 วันนี้". */
export function hitsText(h: WebBlockState['limit_hits'], rel: (iso: string) => string): string {
  if (!h.count) return `ไม่ชนเพดานกันสแปมใน ${h.days} วันนี้`
  return `ชนเพดานกันสแปม ${h.count.toLocaleString('en-US')} ครั้งใน ${h.days} วัน${h.last_at ? ` · ล่าสุด ${rel(h.last_at)}` : ''}`
}

export const STATUS_LABEL: Record<WebBlock['status'], string> = {
  active: 'บล็อกอยู่',
  unblocked: 'ยกเลิกแล้ว',
  replaced: 'เปลี่ยนเป็นการบล็อกใหม่',
  expired: 'หมดเวลาแล้ว',
}

/** One line of history: "7 วัน · โดย มิ้นท์ · 26 ก.ย. 14:30 น. · ยกเลิกโดย ปอย". */
export function historyLine(b: WebBlock, nowMs: number): string {
  const parts = [durationLabel(b), `โดย ${b.by.name}`, bkkDateTime(b.at, nowMs)]
  if (b.lifted?.kind === 'unblock') parts.push(`ยกเลิกโดย ${b.lifted.by.name}`)
  return parts.join(' · ')
}

/** How long the block was made for, from its start and end ("24 ชั่วโมง" / "7 วัน" / "ถาวร"). */
export function durationLabel(b: Pick<WebBlock, 'at' | 'until'>): string {
  if (b.until === null) return 'ถาวร'
  const h = Math.round((Date.parse(b.until) - Date.parse(b.at)) / 3600_000)
  if (h >= 48) return `${Math.round(h / 24)} วัน`
  return `${Math.max(1, h)} ชั่วโมง`
}

export function cleanReason(raw: string): string | null {
  const t = raw.replace(/\s+/g, ' ').trim()
  return t ? t.slice(0, REASON_MAX) : null
}
